import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {useLinkNavigation} from '../../../../../../components/common/navigation/useLinkNavigation';
import {useAppDispatch, useAppSelector} from '../../../../../../helpers/reduxHelpers';
import {
  GithubIntegrationSettingsModel,
  IntegrationSettingsTypesEnum,
} from '../../../../../../models/api/integrationModel';
import {
  signInToIntegrationAccountAction,
  signOutOfIntegrationAsyncAction,
} from '../../../../../../store/data/integrations/integrationsActions';
import {getIsUserGlobalAdmin} from '../../../../../../store/data/teammates/teammatesSelectors';

/*
 * Constants.
 */

const seeHowItWorksLink = 'http://help.frontapp.com/t/186k1y/how-to-enable-and-use-the-github-integration';

/*
 * Props.
 */

interface SettingsAppsConfigureGithubProps {
  configurationSettings?: GithubIntegrationSettingsModel;
}

/*
 * Style.
 */

const StyledSeeHowItWorksWrapperDiv = styled.div`
  display: flex;
  margin-left: -14px;
  margin-bottom: 16px;
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  margin-top: 8px;
`;

/*
 * Component.
 */

export const SettingsAppsConfigureGithub: FC<SettingsAppsConfigureGithubProps> = (props) => {
  const {configurationSettings} = props;
  const {navigateToLink} = useLinkNavigation();
  const dispatch = useAppDispatch();
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);

  const onSignInClick = () => {
    dispatch(signInToIntegrationAccountAction(IntegrationSettingsTypesEnum.GITHUB, 'Github'));
  };

  const onSignOutClick = () => {
    dispatch(signOutOfIntegrationAsyncAction(IntegrationSettingsTypesEnum.GITHUB));
  };

  return (
    <>
      <Trans id="settings-apps-configure-app-dialog-github-description">
        Adds an &quot;Add to Github&quot; item to the conversation menu.
      </Trans>
      <StyledSeeHowItWorksWrapperDiv>
        <Button variant={ButtonVariantsEnum.LINK} onClick={() => navigateToLink(seeHowItWorksLink)}>
          <Trans id="settings-apps-configure-app-dialog-github-see-how-it-works">See how it works</Trans>
        </Button>
      </StyledSeeHowItWorksWrapperDiv>
      {maybeRenderConnectedDetails(configurationSettings, isGlobalAdmin, onSignOutClick)}
      {maybeRenderNotConnectedDetails(configurationSettings, isGlobalAdmin, onSignInClick)}
    </>
  );
};

/*
 * Helpers.
 */

function maybeRenderConnectedDetails(
  configurationSettings: GithubIntegrationSettingsModel | undefined,
  isGlobalAdmin: boolean,
  onSignOutClick: Handler,
) {
  if (!configurationSettings || !configurationSettings.readonly?.login) {
    return null;
  }
  return (
    <>
      <Trans id="settings-apps-configure-app-dialog-github-connected-description">
        Your team is connected to Github as <strong>{configurationSettings.readonly.login}</strong>. Now when
        an issue is closed in a Github repository that <strong>{configurationSettings.readonly.login}</strong>{' '}
        has admin access to, the linked conversations in Front will be reopened automatically.
      </Trans>
      <StyledButtonWrapperDiv>
        <Button
          variant={ButtonVariantsEnum.SECONDARY_DANGER}
          onClick={onSignOutClick}
          isDisabled={!isGlobalAdmin}
        >
          <Trans id="settings-apps-configure-app-dialog-github-sign-out-button">Sign out of Github</Trans>
        </Button>
      </StyledButtonWrapperDiv>
    </>
  );
}

function maybeRenderNotConnectedDetails(
  configurationSettings: GithubIntegrationSettingsModel | undefined,
  isGlobalAdmin: boolean,
  onSignInClick: Handler,
) {
  if (configurationSettings?.readonly?.login) {
    return null;
  }
  return (
    <>
      <Trans id="settings-apps-configure-app-dialog-github-not-connected-description">
        If you sign in here, when an issue is closed in Github, the linked conversations in Front will be
        reopened automatically.
      </Trans>
      <StyledButtonWrapperDiv>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onSignInClick} isDisabled={!isGlobalAdmin}>
          <Trans id="settings-apps-configure-app-dialog-github-sign-in-button">Sign in to Github</Trans>
        </Button>
      </StyledButtonWrapperDiv>
    </>
  );
}
